import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authApiService } from "../../api/api";
import FilterBlock from "../../components/FilterBlock/FilterBlock";
import TableTitle from "../../components/TableTitle/TableTitle";
import { BatteryType } from "../../types/battery.type";
import { formatDate, formatTime } from "../../utils/formatDate";
import {
  batteryHealthTitle,
  getBatteryHealthLevel,
  getBmsCurrentAmps,
  getBmsPackVoltageVolts,
  getBmsSocPercent,
  getEffectiveDeltaVoltage,
} from "../../utils/batteryHealth";
import { getBatteryBmsDisplayTime } from "../../utils/batteryBmsTime";
import AddBatteryModal from "../../components/modals/AddBatteryModal/AddBatteryModal";
import styles from "./styles.module.scss";

type BatterySortField = "lastUpdatedAt" | "deltaVoltage";
type SortDirection = "asc" | "desc";
type BatteryLocationFilter = "all" | string;

const getBatteryLocationDisplayName = (battery: BatteryType) => {
  return battery.lastLocationName?.trim() || battery.lastLocation || "-";
};

const BatteriesPage: React.FC = () => {
  const [batteries, setBatteries] = useState<BatteryType[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sortField, setSortField] = useState<BatterySortField>("lastUpdatedAt");
  const [sortDirection, setSortDirection] = useState<SortDirection>("desc");
  const [locationFilter, setLocationFilter] =
    useState<BatteryLocationFilter>("all");
  const [isAddBatteryModal, setIsAddBatteryModal] = useState(false);
  const navigate = useNavigate();

  const getBatteries = async () => {
    setIsLoading(true);
    try {
      const results = await authApiService.getBatteries();
      if (results) {
        setBatteries(results);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void getBatteries();
  }, []);

  const availableLocationNames = useMemo(() => {
    return Array.from(
      new Set(
        batteries
          .map((battery) => getBatteryLocationDisplayName(battery))
          .filter(Boolean),
      ),
    ).sort((first, second) => first.localeCompare(second, "uk"));
  }, [batteries]);

  useEffect(() => {
    if (locationFilter === "all") {
      return;
    }

    if (!availableLocationNames.includes(locationFilter)) {
      setLocationFilter("all");
    }
  }, [availableLocationNames, locationFilter]);

  const filteredAndSortedBatteries = useMemo(() => {
    const filtered = batteries.filter((battery) => {
      const locationName = getBatteryLocationDisplayName(battery);
      if (locationFilter !== "all" && locationName !== locationFilter) {
        return false;
      }

      const normalizedSearch = searchValue.trim().toLowerCase();
      if (!normalizedSearch) {
        return true;
      }

      return (
        battery.name?.toLowerCase().includes(normalizedSearch) ||
        battery.macAddress?.toLowerCase().includes(normalizedSearch) ||
        battery.lastLocation?.toLowerCase().includes(normalizedSearch) ||
        (battery.lastLocationName?.toLowerCase().includes(normalizedSearch) ?? false)
      );
    });

    return filtered.sort((first, second) => {
      const firstRaw =
        sortField === "lastUpdatedAt"
          ? (() => {
              const t = getBatteryBmsDisplayTime(first);
              return t ? new Date(t).getTime() : 0;
            })()
          : Number(getEffectiveDeltaVoltage(first) ?? Number.NEGATIVE_INFINITY);
      const secondRaw =
        sortField === "lastUpdatedAt"
          ? (() => {
              const t = getBatteryBmsDisplayTime(second);
              return t ? new Date(t).getTime() : 0;
            })()
          : Number(getEffectiveDeltaVoltage(second) ?? Number.NEGATIVE_INFINITY);

      if (firstRaw === secondRaw) {
        return 0;
      }

      const diff = firstRaw > secondRaw ? 1 : -1;
      return sortDirection === "asc" ? diff : -diff;
    });
  }, [batteries, locationFilter, searchValue, sortField, sortDirection]);

  const toggleSort = (field: BatterySortField) => {
    if (sortField === field) {
      setSortDirection((prevState) => (prevState === "asc" ? "desc" : "asc"));
      return;
    }

    setSortField(field);
    setSortDirection("desc");
  };

  return (
    <main className={styles.container}>
      <div className={styles.header}>
        <TableTitle title="Всього акумуляторів" count={filteredAndSortedBatteries.length} />
        <div className={styles.headerControls}>
          <label className={styles.locationFilter}>
            <span>Локація</span>
            <select
              value={locationFilter}
              onChange={(event) =>
                setLocationFilter(event.currentTarget.value as BatteryLocationFilter)
              }
            >
              <option value="all">Всі</option>
              {availableLocationNames.map((locationName) => (
                <option key={locationName} value={locationName}>
                  {locationName}
                </option>
              ))}
            </select>
          </label>
          <FilterBlock
            searchValue={searchValue}
            onSearchChange={(value) => setSearchValue(value)}
            onPressAdd={() => setIsAddBatteryModal(true)}
            addButtonTitle="Додати"
          />
        </div>
      </div>

      {isLoading ? (
        <p className={styles.status}>Завантаження...</p>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Назва</th>
              <th>MAC</th>
              <th>Локація</th>
              <th>Напруга пакета</th>
              <th>
                <button
                  className={styles.sortButton}
                  onClick={() => toggleSort("lastUpdatedAt")}
                >
                  Оновлення BMS
                  {sortField === "lastUpdatedAt" &&
                    (sortDirection === "asc" ? " ↑" : " ↓")}
                </button>
              </th>
              <th>
                <button
                  className={styles.sortButton}
                  onClick={() => toggleSort("deltaVoltage")}
                >
                  ΔV та стан
                  {sortField === "deltaVoltage" &&
                    (sortDirection === "asc" ? " ↑" : " ↓")}
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedBatteries.map((battery) => {
              const deltaVolts = getEffectiveDeltaVoltage(battery);
              const health = getBatteryHealthLevel(deltaVolts);
              const packV = getBmsPackVoltageVolts(battery.bmsLastRawData);
              const bmsCurrent = getBmsCurrentAmps(battery.bmsLastRawData);
              const showChargingPlug =
                bmsCurrent !== undefined && bmsCurrent < 0;
              const soc = getBmsSocPercent(battery.bmsLastRawData);
              const dotClass =
                health === "good"
                  ? styles.healthGood
                  : health === "warn"
                    ? styles.healthWarn
                    : health === "bad"
                      ? styles.healthBad
                      : styles.healthUnknown;

              return (
              <tr
                key={battery.id}
                onClick={() => navigate(`/batteries/${battery.id}`)}
                className={styles.row}
              >
                <td>{battery.name || "-"}</td>
                <td>{battery.macAddress || "-"}</td>
                <td>
                  {getBatteryLocationDisplayName(battery)}
                </td>
                <td>
                  {packV !== undefined ? `${packV.toFixed(2)} В` : "—"}
                </td>
                <td>
                  {(() => {
                    const t = getBatteryBmsDisplayTime(battery);
                    return t
                      ? `${formatDate(t)} ${formatTime(t)}`
                      : "-";
                  })()}
                </td>
                <td>
                  <div className={styles.deltaCell}>
                    {showChargingPlug && (
                      <span
                        className={styles.chargingPlug}
                        title="Заряд: струм відʼємний"
                        aria-label="Заряд: струм відʼємний"
                      >
                        🔌
                      </span>
                    )}
                    <span
                      className={`${styles.healthDot} ${dotClass}`}
                      title={batteryHealthTitle(health, deltaVolts)}
                      aria-label={batteryHealthTitle(health, deltaVolts)}
                    />
                    <span className={styles.deltaText}>
                      {deltaVolts !== null ? deltaVolts.toFixed(3) : "—"}
                      {soc !== undefined && (
                        <span className={styles.soc}> · SoC {soc}%</span>
                      )}
                    </span>
                  </div>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {isAddBatteryModal && (
        <AddBatteryModal
          onClose={() => setIsAddBatteryModal(false)}
          onApply={() => {
            void getBatteries();
          }}
        />
      )}
    </main>
  );
};

export default BatteriesPage;
